import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import type RequestError from '@app/src/errors/RequestError'
import useApi from '@app/src/hooks/api'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { SavedView, SavedViewSaveModel } from '@app/src/types/savedViews'
import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export const SavedViewQueryKey = {
  all: () => [FetchKey.SavedViews] as const,
}

/** All views visible to the caller for the current solution+org, across pages (drives the nav). */
export const useAllSavedViews = (
  options?: Omit<UseQueryOptions<SavedView[], RequestError>, 'queryKey' | 'queryFn'>,
) => {
  const { get } = useApi()
  return useQuery<SavedView[], RequestError>({
    queryKey: SavedViewQueryKey.all(),
    queryFn: async () => (await get<SavedView[]>({ url: endpoints.savedViews })) ?? [],
    ...options,
  })
}

const useInvalidateSavedViews = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [FetchKey.SavedViews] })
}

export const useCreateSavedView = (
  options?: Omit<UseMutationOptions<SavedView, RequestError, SavedViewSaveModel>, 'mutationFn'>,
) => {
  const { post } = useApi()
  const invalidate = useInvalidateSavedViews()
  const { showErrorNotification } = useErrorNotification()

  return useMutation<SavedView, RequestError, SavedViewSaveModel>({
    ...options,
    mutationFn: body => post<SavedView, SavedViewSaveModel>({ url: endpoints.savedViews, body }),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    onError: (...args) => {
      showErrorNotification({ requestError: args[0] })
      options?.onError?.(...args)
    },
  })
}

type UpdateSavedViewParams = { id: number; body: SavedViewSaveModel }

export const useUpdateSavedView = (
  options?: Omit<UseMutationOptions<SavedView, RequestError, UpdateSavedViewParams>, 'mutationFn'>,
) => {
  const { put } = useApi()
  const invalidate = useInvalidateSavedViews()
  const { showErrorNotification } = useErrorNotification()

  return useMutation<SavedView, RequestError, UpdateSavedViewParams>({
    ...options,
    mutationFn: ({ id, body }) => put<SavedView, SavedViewSaveModel>({ url: endpoints.savedView(id), body }),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    onError: (...args) => {
      showErrorNotification({ requestError: args[0] })
      options?.onError?.(...args)
    },
  })
}

export const useDeleteSavedView = (options?: Omit<UseMutationOptions<void, RequestError, number>, 'mutationFn'>) => {
  const api = useApi()
  const invalidate = useInvalidateSavedViews()
  const { showErrorNotification } = useErrorNotification()

  return useMutation<void, RequestError, number>({
    ...options,
    mutationFn: id => api.delete<void, never>({ url: endpoints.savedView(id) }),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    onError: (...args) => {
      showErrorNotification({ requestError: args[0] })
      options?.onError?.(...args)
    },
  })
}
